import { MAX_SIZE_UPLOAD, SystemMessage } from "@app/constants";
import { PortalDialogProps } from "@app/services/modal.service";
import { CreateVoiceForm, Voice } from "@app/types";
import { Form, Formik, FormikContextType } from "formik";
import { ChangeEvent, createRef, useEffect, useRef } from "react";
import Modal from "../modal/modal.component";
import { FormControl } from "../form-control";
import Input from "../input";
import { Images } from "@assets/images";
import { createVoiceValidationSchema } from "@app/validations/voice.validation";
import Button from "../button";

interface VoiceModalProps {
  isEdit: boolean;
  voice: Voice;
}

function VoiceModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<VoiceModalProps>) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const formRef = createRef<FormikContextType<CreateVoiceForm>>();

  const onSubmit = (values: { name: string }) => {
    if (!formRef.current) return;

    if (!inputFileRef.current?.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);

      return;
    }

    const file = inputFileRef.current.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);

      return;
    }

    const voice = {
      name: values.name,
      audio: file,
    };

    portalDialogRef.close({ voice });
  };

  const onAddFileClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRef.current) return;

    if (!event.target.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);
      formRef.current.setFieldValue("filename", "");

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);
      formRef.current.setFieldValue("filename", "");

      return;
    }

    formRef.current.setFieldError("filename", undefined);
    formRef.current.setFieldValue("filename", file.name);
  };

  useEffect(() => {
    if (portalData?.voice && formRef.current) {
      formRef.current.setValues({
        name: portalData.voice.name,
        filename: "",
      });
    }
  }, [portalData?.voice]);

  return (
    <Modal onCancel={portalDialogRef.close}>
      <div className="w-[423px] py-7 px-6">
        <div className="text-black font-semibold text-xl">
          {portalData?.isEdit ? "Chỉnh sửa" : "Thêm voice"}
        </div>
        <Formik
          innerRef={formRef}
          initialValues={{
            name: "",
            filename: "",
          }}
          onSubmit={onSubmit}
          validationSchema={createVoiceValidationSchema}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <FormControl name="name">
              <Input className="mt-7" placeholder="Tên voice" />
            </FormControl>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="File voice"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileClick}
              />
            </FormControl>
            <input
              ref={inputFileRef}
              type="file"
              name="file"
              hidden
              onChange={handleFileChange}
            />
            <div className="mt-11 flex justify-center gap-4">
              <Button
                size="xs"
                label="Hủy"
                theme="secondary"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
                onClick={portalDialogRef.close}
              />
              <Button
                type="submit"
                size="xs"
                label={portalData?.isEdit ? "Lưu" : "Thêm"}
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}

export default VoiceModal;
